import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Spin,
} from "antd";
import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useForm } from "antd/lib/form/Form";
import { useHistory, useLocation } from "react-router-dom";

import { Locale } from "../../utils";
import {
  handleMutationError,
  showNotificationWithIcon,
} from "../../utils/helpers/helpers";
import { getUserInfo } from "../../utils/redux/reducers/User";
import { BreadCrumb } from "../../components/main/LayoutComponents";

import "./AddOrEditVideo.scss";
import {
  create,
  getList,
  getOne,
  update,
} from "../../utils/http/dataProvider";
import ImageUpload from "../../components/media/ImageUpload";
import VideoUpload from "../../components/media/VideoUpload";
import TranslateBtn from "../../components/product/TranslateBtn";

const i18n = Locale.getInstance();
const { Option } = Select;

const formatDuration = (seconds) => {
  if (!seconds) return "00:00";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
};

const AddOrEditVideo = () => {
  const user = useSelector((state) => getUserInfo(state));
  const [loading, setLoading] = useState(false);
  const [videoDeleted, setVideoDeleted] = useState(false);
  const queryClient = useQueryClient();
  const [videoForm] = useForm();
  const history = useHistory();
  const location = useLocation();

  const id = location?.state?.id;

  const { data, isLoading } = useQuery(
    ["video", id, user.token],
    () => getOne("video", { id, token: user.token }),
    {
      enabled: !!id,
    }
  );

  const { data: categoriesData } = useQuery(
    ["video-categories", user.token],
    () =>
      getList("video-category", {
        pagination: {
          perPage: 1000,
          page: 1,
        },
        sort: {
          field: "id",
          order: "desc",
        },
        token: user.token,
      })
  );

  const videoCategories = categoriesData?.data ?? [];

  useEffect(() => {
    if (id && data?.id === id) {
      const values = {
        zh_name: data.zh_name,
        ug_name: data.ug_name,
        ug_image: data?.ug_image ? [{ file_path: data.ug_image, url: data.ug_image }] : [],
        zh_image: data?.zh_image ? [{ file_path: data.zh_image, url: data.zh_image }] : [],
        media_id: data?.media ? [data.media] : [],
        video_category_id:
          data.video_category_id != null ? data.video_category_id : undefined,
        status: data.status,
        order: data.order,
        default_views: data.default_views || 0,
        views: data.views || 0,
        default_favourites: data.default_favourites || 0,
        favorites: data.favorites || 0,
      };
      videoForm.setFieldsValue(values);
    }
    if (!id) {
      const values = {
        zh_name: "",
        ug_name: "",
        ug_image: [],
        zh_image: [],
        media_id: [],
        video_category_id: undefined,
        status: 1,
        order: "",
        default_views: 0,
        views: 0,
        default_favourites: 0,
        favorites: 0,
      };
      videoForm.setFieldsValue(values);
    }
  }, [data, id, videoForm]);

  const addVideoMutation = useMutation(create, {
    onSuccess: () => {
      queryClient.invalidateQueries("videos");
      showNotificationWithIcon(i18n.t("media.saveFinished"), "success");
      setLoading(false);
      history.goBack();
    },
    onError: (error) => {
      handleMutationError(error);
      setLoading(false);
    },
  });

  const updateVideoMutation = useMutation(update, {
    onSuccess: () => {
      queryClient.invalidateQueries("videos");
      queryClient.invalidateQueries(["video", id]);
      showNotificationWithIcon(i18n.t("media.saveFinished"), "success");
      setLoading(false);
      history.goBack();
    },
    onError: (error) => {
      handleMutationError(error);
      setLoading(false);
    },
  });

  const onFinish = (values) => {
    // Transform image paths from ImageUpload format
    if (values.zh_image && values.zh_image.length > 0) {
      values["zh_image"] = values["zh_image"][0]["file_path"];
    } else {
      values["zh_image"] = null;
    }
    
    if (values.ug_image && values.ug_image.length > 0) {
      values["ug_image"] = values["ug_image"][0]["file_path"];
    } else {
      values["ug_image"] = null;
    }

    // Transform video media_id from VideoUpload format
    if (values.media_id && values.media_id.length > 0) {
      values["media_id"] = values["media_id"][0]["id"];
    } else if (videoDeleted) {
      values["media_id"] = null;
    } else {
      // Keep existing media_id if not deleted and not provided
      if (id && data?.media_id) {
        values["media_id"] = data.media_id;
      } else {
        values["media_id"] = null;
      }
    }

    // Ensure numeric fields are numbers
    if (values.default_views === undefined || values.default_views === null) {
      values.default_views = 0;
    }
    if (values.views === undefined || values.views === null) {
      values.views = 0;
    }
    if (values.default_favourites === undefined || values.default_favourites === null) {
      values.default_favourites = 0;
    }
    if (values.favorites === undefined || values.favorites === null) {
      values.favorites = 0;
    }

    values.video_category_id =
      values.video_category_id === undefined || values.video_category_id === ""
        ? null
        : values.video_category_id;

    setLoading(true);
    const params = {
      data: values,
      token: user.token,
    };

    if (id) {
      updateVideoMutation.mutate({
        resource: "video",
        params: { ...params, id },
      });
    } else {
      addVideoMutation.mutate({ resource: "video", params });
    }
  };

  const onFinishFailed = (errorInfo) => {
    setLoading(false);
    showNotificationWithIcon(errorInfo);
  };

  const breadCrumbData = [
    {
      href: "/main/videos",
      title: i18n.t("video.title"),
    },
    {
      title: id ? i18n.t("video.edit") : i18n.t("video.add"),
    },
  ];

  if (isLoading) {
    return <Spin spinning={isLoading} />;
  }

  return (
    <div className="edit-video-page">
      <BreadCrumb data={breadCrumbData} />
      <Spin spinning={loading}>
        <Form
          form={videoForm}
          className="video-form"
          name="basic"
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 20 }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
        >
          <Card title={i18n.t("course.basicInfo")} className="basic-info info">
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => (
                <Form.Item
                  name="ug_name"
                  label={i18n.t("course.ugName")}
                  rules={[
                    {
                      required: true,
                      message: i18n.t("productEdit.standardRequired"),
                    },
                  ]}
                >
                  <Input
                    addonAfter={
                      <TranslateBtn
                        context={getFieldValue("ug_name")}
                        onFinish={(value) =>
                          setFieldsValue({
                            zh_name: value,
                          })
                        }
                        from="ug"
                        to="zh"
                      />
                    }
                  />
                </Form.Item>
              )}
            </Form.Item>

            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => (
                <Form.Item
                  name="zh_name"
                  label={i18n.t("course.zhName")}
                  rules={[
                    {
                      required: true,
                      message: i18n.t("productEdit.standardRequired"),
                    },
                  ]}
                >
                  <Input
                    addonAfter={
                      <TranslateBtn
                        context={getFieldValue("zh_name")}
                        onFinish={(value) =>
                          setFieldsValue({
                            ug_name: value,
                          })
                        }
                        from="zh"
                        to="ug"
                      />
                    }
                  />
                </Form.Item>
              )}
            </Form.Item>

            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => (
                <Form.Item
                  name="zh_image"
                  label={i18n.t("video.zhCover")}
                >
                  <ImageUpload
                    images={getFieldValue("zh_image")}
                    setSelectedImages={(image) => {
                      setFieldsValue({
                        zh_image: image,
                      });
                    }}
                  />
                </Form.Item>
              )}
            </Form.Item>

            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => (
                <Form.Item
                  name="ug_image"
                  label={i18n.t("video.ugCover")}
                >
                  <ImageUpload
                    images={getFieldValue("ug_image")}
                    setSelectedImages={(image) => {
                      setFieldsValue({
                        ug_image: image,
                      });
                    }}
                  />
                </Form.Item>
              )}
            </Form.Item>

            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => (
                <Form.Item
                  name="media_id"
                  label={i18n.t("video.videoFile")}
                >
                  <VideoUpload
                    video={videoDeleted ? null : getFieldValue("media_id")}
                    setSelectedVideo={(video) => {
                      setFieldsValue({
                        media_id: video,
                      });
                      setVideoDeleted(false);
                    }}
                    setDeleted={() => {
                      setVideoDeleted(true);
                    }}
                  />
                </Form.Item>
              )}
            </Form.Item>

            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const media = getFieldValue("media_id");
                const duration = media && media.length > 0 ? media[0].duration : (data?.duration || null);
                return duration ? (
                  <Form.Item label={i18n.t("video.duration")}>
                    <span>{formatDuration(duration)}</span>
                  </Form.Item>
                ) : null;
              }}
            </Form.Item>
          </Card>

          <Card title={i18n.t("course.album.settings")} className="settings-info info">
            <Form.Item
              name="video_category_id"
              label={i18n.t("course.selectCategory")}
            >
              <Select
                style={{ width: "100%", maxWidth: 360 }}
                placeholder={i18n.t("course.selectCategory")}
                allowClear
              >
                {videoCategories.map((cat) => (
                  <Option key={cat.id} value={cat.id}>
                    {i18n.getPropNameByLocale(cat, "name")}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="status"
              label={i18n.t("course.album.status")}
              initialValue={1}
            >
              <Radio.Group>
                <Radio value={1}>{i18n.t("course.album.statusOnShelf")}</Radio>
                <Radio value={0}>{i18n.t("course.album.statusOffShelf")}</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="order"
              label={i18n.t("course.album.order")}
            >
              <InputNumber style={{ width: 120 }} min={0} />
            </Form.Item>
          </Card>

          <Card title={i18n.t("video.statistics")} className="statistics-info info">
            <Form.Item
              name="default_views"
              label={i18n.t("video.defaultViews")}
            >
              <InputNumber style={{ width: 200 }} min={0} />
            </Form.Item>

            <Form.Item
              name="views"
              label={i18n.t("video.views")}
            >
              <InputNumber style={{ width: 200 }} min={0} />
            </Form.Item>

            <Form.Item
              name="default_favourites"
              label={i18n.t("video.defaultFavourites")}
            >
              <InputNumber style={{ width: 200 }} min={0} />
            </Form.Item>

            <Form.Item
              name="favorites"
              label={i18n.t("video.favorites")}
            >
              <InputNumber style={{ width: 200 }} min={0} />
            </Form.Item>
          </Card>

          <Form.Item className="submit">
            <Button type="primary" htmlType="submit">
              {i18n.t("productEdit.saveAndBack")}
            </Button>
          </Form.Item>
        </Form>
      </Spin>
    </div>
  );
};

export default AddOrEditVideo;

